import React, { useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import classNames from "classnames";
import { formatDate } from "../../utils";
import {
  formatOperationalWindowEndForDisplay,
  formatOperationalWindowStartForDisplay,
} from "../../utils/date.utils";

/**
 * GroupedPayDateSummaryTable - Displays grouped summary by payDate for payout reports
 * Shows totals for each payDate group
 */
const GroupedPayDateSummaryTable = ({ data, reportConfig }) => {
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "0";
    return value.toLocaleString();
  };

  // Extract grouped data - group by _groupPayDate
  const groupedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Check if data has grouping fields
    const hasGrouping = data.some(
      (item) => item._groupPayDate || item._groupPayDateString
    );

    if (!hasGrouping) return [];

    // Group by payDate and calculate totals from individual payout virtual fields
    // Since virtual fields are calculated after aggregation, we calculate totals here
    const groups = {};
    data.forEach((item) => {
      // Prefer _groupPayDateString (formatted date string from backend - YYYY-MM-DD)
      // Fallback to formatting _groupPayDate if it exists
      let groupKey = item._groupPayDateString;
      let formattedPayDate = null;
      
      // Get the actual date value for formatting
      const dateValue = item._groupPayDate || item.payDate;
      
      if (groupKey) {
        // If we have YYYY-MM-DD format, convert to Date and format to MM-DD-YYYY
        // Parse YYYY-MM-DD format correctly
        const dateParts = groupKey.split('-');
        const date = dateParts.length === 3 
          ? new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]))
          : new Date(groupKey);
        formattedPayDate = formatDate(date, null, true); // MM-DD-YYYY format
      } else if (dateValue) {
        // Format the date - handle both Date objects and date strings
        const date =
          dateValue instanceof Date
            ? dateValue
            : new Date(dateValue);
        formattedPayDate = formatDate(date, null, true); // MM-DD-YYYY format
        groupKey = formattedPayDate; // Use formatted date as group key
      }

      if (!groupKey) return;

      // Initialize group if it doesn't exist
      if (!groups[groupKey]) {
        // Get manifest dates from group fields (same for all items in group)
        const manifestStartDate = item._groupManifestStartDate || null;
        const manifestEndDate = item._groupManifestEndDate || null;
        
        groups[groupKey] = {
          payDate: dateValue,
          payDateString: formattedPayDate || groupKey, // Store MM-DD-YYYY format
          totalPayouts: 0,
          totalAmount: 0,
          totalMileage: 0,
          totalHours: 0,
          totalMileageTotal: 0,
          totalHourlyTotal: 0,
          totalSupplementalTotal: 0,
          manifestStartDate: manifestStartDate,
          manifestEndDate: manifestEndDate,
        };
      }

      // Sum up values from individual payouts (virtual fields are now calculated)
      // Get numeric values, defaulting to 0 if undefined or null
      const getNumericValue = (val) => {
        if (val === null || val === undefined || isNaN(val)) return 0;
        return typeof val === "number" ? val : parseFloat(val) || 0;
      };

      // Always sum individual payout values since backend group totals may not be accurate
      // Virtual fields are calculated after aggregation, so they're available here
      groups[groupKey].totalPayouts += 1;
      
      // Sum virtual fields from individual payouts
      // If virtual fields aren't available, calculate from nested arrays (legs/shifts)
      const totalAmount = getNumericValue(item.total);
      const totalMileage = getNumericValue(item.totalMileage);
      const totalHours = getNumericValue(item.hours);
      const mileageTotal = getNumericValue(item.mileageTotal);
      const hourlyTotal = getNumericValue(item.hourlyTotal);
      const supplementalTotal = getNumericValue(item.supplementalTotal);
      
      // If virtual fields are 0 or missing, try calculating from nested arrays
      let calculatedTotalMileage = totalMileage;
      let calculatedHours = totalHours;
      let calculatedMileageTotal = mileageTotal;
      let calculatedHourlyTotal = hourlyTotal;
      
      if ((totalMileage === 0 || !item.totalMileage) && item.legs && Array.isArray(item.legs)) {
        // Calculate totalMileage from legs
        calculatedTotalMileage = item.legs.reduce((sum, leg) => {
          if (leg.useCustomManifestMileage) {
            return sum + (leg.customManifestMileage || 0);
          }
          const route = Array.isArray(leg.route) ? leg.route[0] : leg.route;
          const mileage = route?.mileage || route?.defaultMileage || 0;
          return sum + mileage;
        }, 0);
      }
      
      if ((totalHours === 0 || !item.hours) && (item.legs || item.shifts)) {
        // Calculate hours from legs (waitTime.total) and shifts (totalWorkTime)
        let legHours = 0;
        if (item.legs && Array.isArray(item.legs)) {
          legHours = item.legs.reduce((sum, leg) => {
            return sum + (leg.waitTime?.total || 0);
          }, 0) / (60 * 60 * 1000); // Convert milliseconds to hours
        }
        
        let shiftHours = 0;
        if (item.shifts && Array.isArray(item.shifts)) {
          shiftHours = item.shifts.reduce((sum, shift) => {
            return sum + (shift.totalWorkTime || 0);
          }, 0) / (60 * 60 * 1000); // Convert milliseconds to hours
        }
        
        calculatedHours = legHours + shiftHours;
      }
      
      if ((mileageTotal === 0 || !item.mileageTotal) && item.legs && Array.isArray(item.legs)) {
        // Calculate mileageTotal from legs (payoutMileageTotal)
        calculatedMileageTotal = item.legs.reduce((sum, leg) => {
          // Use calculatedPayoutMileageTotal if available
          if (leg.calculatedPayoutMileageTotal !== undefined && leg.calculatedPayoutMileageTotal !== null) {
            return sum + leg.calculatedPayoutMileageTotal;
          }
          
          // Calculate from payoutRate and mileage
          if (leg.payoutType === "Flat") {
            return sum;
          }
          
          let mileage = 0;
          if (leg.useCustomManifestMileage) {
            mileage = leg.customManifestMileage || 0;
          } else {
            const route = Array.isArray(leg.route) ? leg.route[0] : leg.route;
            mileage = route?.mileage || route?.defaultMileage || 0;
          }
          
          return sum + ((leg.payoutRate || 0) * mileage);
        }, 0);
      }
      
      if ((hourlyTotal === 0 || !item.hourlyTotal) && item.shifts && Array.isArray(item.shifts)) {
        // Calculate hourlyTotal from shifts
        calculatedHourlyTotal = item.shifts.reduce((sum, shift) => {
          return sum + (shift.payoutHourlyTotal || 0);
        }, 0);
      }
      
      // Use calculated values (prefer virtual fields, fallback to calculated from arrays)
      groups[groupKey].totalAmount += totalAmount;
      groups[groupKey].totalMileage += calculatedTotalMileage;
      groups[groupKey].totalHours += calculatedHours;
      groups[groupKey].totalMileageTotal += calculatedMileageTotal;
      groups[groupKey].totalHourlyTotal += calculatedHourlyTotal;
      groups[groupKey].totalSupplementalTotal += supplementalTotal;
    });

    // Convert to array and sort by payDate descending
    return Object.values(groups).sort((a, b) => {
      const dateA = a.payDate ? new Date(a.payDate) : new Date(0);
      const dateB = b.payDate ? new Date(b.payDate) : new Date(0);
      return dateB - dateA; // Newest first
    });
  }, [data]);

  // Build table data
  const tableData = useMemo(() => {
    if (groupedData.length === 0) return [];

    const rows = [];

    // Add data rows
    groupedData.forEach((group) => {
      // Format manifest date range if available
      let manifestDateRange = "-";
      if (group.manifestStartDate && group.manifestEndDate) {
        const startDate = formatOperationalWindowStartForDisplay(
          group.manifestStartDate,
        );
        const endDate = formatOperationalWindowEndForDisplay(
          group.manifestEndDate,
        );
        manifestDateRange = `${startDate} - ${endDate}`;
      } else if (group.manifestStartDate) {
        manifestDateRange = formatOperationalWindowStartForDisplay(
          group.manifestStartDate,
        );
      }

      rows.push({
        type: "data",
        payDate: group.payDateString,
        manifestDateRange: manifestDateRange,
        totalPayouts: group.totalPayouts,
        totalAmount: group.totalAmount,
        totalMileage: group.totalMileage,
        totalHours: group.totalHours,
        totalMileageTotal: group.totalMileageTotal,
        totalHourlyTotal: group.totalHourlyTotal,
        totalSupplementalTotal: group.totalSupplementalTotal,
      });
    });

    // Add grand total row
    if (groupedData.length > 0) {
      const grandTotal = groupedData.reduce(
        (acc, group) => ({
          totalPayouts: acc.totalPayouts + group.totalPayouts,
          totalAmount: acc.totalAmount + group.totalAmount,
          totalMileage: acc.totalMileage + group.totalMileage,
          totalHours: acc.totalHours + group.totalHours,
          totalMileageTotal: acc.totalMileageTotal + group.totalMileageTotal,
          totalHourlyTotal: acc.totalHourlyTotal + group.totalHourlyTotal,
          totalSupplementalTotal:
            acc.totalSupplementalTotal + group.totalSupplementalTotal,
        }),
        {
          totalPayouts: 0,
          totalAmount: 0,
          totalMileage: 0,
          totalHours: 0,
          totalMileageTotal: 0,
          totalHourlyTotal: 0,
          totalSupplementalTotal: 0,
        }
      );

      rows.push({ type: "section", label: "Grand Total" });
      rows.push({
        type: "data",
        payDate: "All Dates",
        manifestDateRange: "-",
        totalPayouts: grandTotal.totalPayouts,
        totalAmount: grandTotal.totalAmount,
        totalMileage: grandTotal.totalMileage,
        totalHours: grandTotal.totalHours,
        totalMileageTotal: grandTotal.totalMileageTotal,
        totalHourlyTotal: grandTotal.totalHourlyTotal,
        totalSupplementalTotal: grandTotal.totalSupplementalTotal,
      });
    }

    return rows;
  }, [groupedData]);

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "payDate",
        header: "Pay Date",
        cell: ({ row }) => {
          const isSection = row.original.type === "section";
          return (
            <span
              style={{
                fontWeight: isSection ? "bold" : "normal",
                fontSize: "8px",
              }}
            >
              {isSection ? row.original.label : row.original.payDate || "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "manifestDateRange",
        header: "Manifest Dates",
        cell: ({ row }) => {
          const isSection = row.original.type === "section";
          return (
            <span
              style={{
                fontWeight: isSection ? "bold" : "normal",
                fontSize: "8px",
              }}
            >
              {isSection ? "" : row.original.manifestDateRange || "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "totalPayouts",
        header: "Payouts",
        cell: ({ row }) => {
          const isSection = row.original.type === "section";
          return (
            <span
              style={{
                fontWeight: isSection ? "bold" : "normal",
                fontSize: "8px",
                textAlign: "right",
              }}
            >
              {isSection
                ? ""
                : row.original.totalPayouts !== undefined
                ? formatNumber(row.original.totalPayouts)
                : "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "totalMileage",
        header: "Mileage",
        cell: ({ row }) => {
          const isSection = row.original.type === "section";
          return (
            <span
              style={{
                fontWeight: isSection ? "bold" : "normal",
                fontSize: "8px",
                textAlign: "right",
              }}
            >
              {isSection
                ? ""
                : row.original.totalMileage !== undefined
                ? formatNumber(row.original.totalMileage)
                : "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "totalHours",
        header: "Hours",
        cell: ({ row }) => {
          const isSection = row.original.type === "section";
          return (
            <span
              style={{
                fontWeight: isSection ? "bold" : "normal",
                fontSize: "8px",
                textAlign: "right",
              }}
            >
              {isSection
                ? ""
                : row.original.totalHours !== undefined
                ? formatNumber(row.original.totalHours.toFixed(2))
                : "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "totalMileageTotal",
        header: "Mileage Total",
        cell: ({ row }) => {
          const isSection = row.original.type === "section";
          return (
            <span
              style={{
                fontWeight: isSection ? "bold" : "normal",
                fontSize: "8px",
                textAlign: "right",
              }}
            >
              {isSection
                ? ""
                : row.original.totalMileageTotal !== undefined
                ? formatCurrency(row.original.totalMileageTotal)
                : "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "totalHourlyTotal",
        header: "Hourly Total",
        cell: ({ row }) => {
          const isSection = row.original.type === "section";
          return (
            <span
              style={{
                fontWeight: isSection ? "bold" : "normal",
                fontSize: "8px",
                textAlign: "right",
              }}
            >
              {isSection
                ? ""
                : row.original.totalHourlyTotal !== undefined
                ? formatCurrency(row.original.totalHourlyTotal)
                : "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "totalSupplementalTotal",
        header: "Supplemental",
        cell: ({ row }) => {
          const isSection = row.original.type === "section";
          return (
            <span
              style={{
                fontWeight: isSection ? "bold" : "normal",
                fontSize: "8px",
                textAlign: "right",
              }}
            >
              {isSection
                ? ""
                : row.original.totalSupplementalTotal !== undefined
                ? formatCurrency(row.original.totalSupplementalTotal)
                : "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "totalAmount",
        header: "Total Amount",
        cell: ({ row }) => {
          const isSection = row.original.type === "section";
          return (
            <span
              style={{
                fontWeight: isSection ? "bold" : "normal",
                fontSize: "8px",
                textAlign: "right",
              }}
            >
              {isSection
                ? ""
                : row.original.totalAmount !== undefined
                ? formatCurrency(row.original.totalAmount)
                : "-"}
            </span>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const tableClasses = classNames("border-collapse border border-gray-300 w-full");
  const theadClasses = classNames("bg-gray-100 font-times-new-roman");
  const trClasses = "";
  const thClasses = classNames(
    "bg-gray-100 py-2 text-left px-1 border border-gray-300 whitespace-nowrap"
  );
  const tdClasses = classNames("py-2 px-1 border border-gray-300");

  if (tableData.length === 0) {
    return null;
  }

  return (
    <div id="grouped-paydate-summary-container">
      <h3
        style={{
          fontSize: "12px",
          fontWeight: "bold",
          marginBottom: "8px",
          marginTop: "16px",
        }}
        className="grouped-paydate-header"
      >
        Grouped by Pay Date
      </h3>
      <table id="grouped-paydate-summary" className={tableClasses}>
      <thead className={theadClasses}>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr className={trClasses} key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th key={header.id} className={thClasses}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => {
          const isSection = row.original.type === "section";
          return (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => {
                return (
                  <td
                    key={cell.id}
                    className={tdClasses}
                    style={{
                      fontWeight: isSection ? "bold" : "normal",
                      backgroundColor: isSection ? "#f3f4f6" : "white",
                    }}
                  >
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
};

export default GroupedPayDateSummaryTable;
